import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Empty, Flex, Table } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import Button from '../../../../common/Button'
import NoRecordsFound from '../../../../common/NoRecordsFound'
import { formatEmptyValue } from '../../../../../utils/common'
import EllipsisTooltip from '../../../../common/EllipsisTooltip'
import RecordsCount from '../../../../common/RecordsCount'
import useTableScrollY, { tableAreaStyle, emptyStateStyle } from '../useTableScrollY'

const SearchResultsTable = ({ type, rows, onSelect }) => {
  const intl = useIntl()
  const [tableAreaRef, scrollY] = useTableScrollY()
  const dataSource = useMemo(
    () =>
      (rows || []).map((row, index) => ({
        ...row,
        key: `search-result-${type}-${index}`,
      })),
    [rows, type]
  )

  const columns = useMemo(() => {
    const sortText = (field) => (a, b) =>
      (a?.[field] || '').localeCompare(b?.[field] || '')

    const idColumn = {
      title: (
        <EllipsisTooltip className='ellipsis'>{intl.formatMessage({ id: 'functionalDiagram.idColumn', defaultMessage: 'ID' })}</EllipsisTooltip>
      ),
      dataIndex: 'name',
      sorter: sortText('name'),
      render: (text) => (
        <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>
      ),
    }

    const aliasColumn = {
      title: (
        <EllipsisTooltip className='ellipsis'>{intl.formatMessage({ id: 'functionalDiagram.alias' })}</EllipsisTooltip>
      ),
      dataIndex: 'alias',
      width: 80,
      sorter: sortText('alias'),
      render: (text) => (
        <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>
      ),
    }

    const circuitColumn = {
      title: (
        <EllipsisTooltip className='ellipsis'>{intl.formatMessage({ id: 'functionalDiagram.circuit' })}</EllipsisTooltip>
      ),
      dataIndex: 'circuit',
      sorter: sortText('circuit'),
      render: (text) => (
        <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>
      ),
    }

    const descriptionColumn = {
      title: (
        <EllipsisTooltip className='ellipsis'>{intl.formatMessage({ id: 'generic.description' })}</EllipsisTooltip>
      ),
      dataIndex: 'description',
      sorter: sortText('description'),
      render: (text) => (
        <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>
      ),
    }

    const actionColumn = {
      title: '',
      width: 70,
      render: (_, record) => (
        <Flex gap={8} align="center">
          <Button
            type="link"
            className="link-button"
            onClick={() => onSelect(record)}
            testId="fd-search-select"
          >
            {intl.formatMessage({ id: 'forms.select' })}
          </Button>
        </Flex>
      ),
    }

    return type === 'circuits'
      ? [idColumn, aliasColumn, circuitColumn, descriptionColumn, actionColumn]
      : [idColumn, aliasColumn, descriptionColumn, actionColumn]
  }, [type, onSelect, intl])

  return (
    <div ref={tableAreaRef} style={{ ...tableAreaStyle, height: '100%' }}>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        showSorterTooltip={false}
        scroll={{ y: scrollY > 0 ? scrollY : undefined }}
        locale={{
          emptyText: (
            <div style={emptyStateStyle(scrollY)}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
    </div>
  )
}

export default memo(SearchResultsTable)
